import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Tooltip, TextField, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../atomic/molecules';
import StudioPromptQuery from '../../../services/query/StudioPromptQuery';
import StudioPromptCommand from '../../../services/command/StudioPromptCommand';
import { StudioPrompt } from '../../../types/StudioPrompt';
import { StudioPromptDrawer } from './components/StudioPromptDrawer';
import Swal from 'sweetalert2';
import './StudioPrompt.scss';

export const StudioPromptList = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<StudioPrompt[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchName, setSearchName] = useState('');
    const [filterIsActive, setFilterIsActive] = useState<string>('all');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState<StudioPrompt | null>(null);

    useEffect(() => {
        fetchData();
    }, [currentPage, pageSize, filterIsActive]);

    const fetchData = async (page: number = currentPage) => {
        try {
            setIsLoading(true);
            const params: any = {
                pageNumber: page + 1,
                pageSize: pageSize,
            };

            params.name = searchName;
            if (filterIsActive === 'active') params.isActive = true;
            if (filterIsActive === 'inactive') params.isActive = false;

            const response = await StudioPromptQuery.GetStudioPrompts(params);
            setData(response.model?.items || []);
            setTotalCount(response.model?.totalCount || 0);
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.anErrorOccured'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(0);
        if (currentPage === 0) {
            fetchData(0);
        }
    };

    const handleClearFilters = () => {
        setSearchName('');
        setFilterIsActive('all');
        setCurrentPage(0);
    };

    const handleAdd = () => {
        setSelectedPrompt(null);
        setDrawerOpen(true);
    };

    const handleEdit = (prompt: StudioPrompt) => {
        setSelectedPrompt(prompt);
        setDrawerOpen(true);
    };

    const handleDelete = async (id: number, name: string) => {
        const result = await Swal.fire({
            title: t('general.areYouSure'),
            text: t('studyoPrompt.deleteConfirmationText', { name }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        });

        if (result.isConfirmed) {
            try {
                setIsLoading(true);
                await StudioPromptCommand.DeleteStudioPrompt(id);
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('studyoPrompt.deleteSuccess'),
                    timer: 1500,
                    showConfirmButton: false,
                });
                fetchData();
            } catch {
                Swal.fire({
                    icon: 'error',
                    title: t('general.anErrorOccured'),
                    text: t('studyoPrompt.deleteError'),
                });
                setIsLoading(false);
            }
        }
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedPrompt(null);
    };

    const handleSaveSuccess = () => {
        handleDrawerClose();
        fetchData();
    };

    const breadcrumbList = [{ label: t('general.home'), path: '/home' }, { label: t('studyoPrompt.title') }];

    const columns = [
        {
            field: 'name',
            headerName: t('studyoPrompt.name'),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'promptType',
            headerName: t('studyoPrompt.promptTypeName'),
            width: 150,
            valueGetter: (params: any) => {
                return t(`studyoPrompt.promptTypes.${params || ''}`);
            },
        },
        {
            field: 'category',
            headerName: t('studyoPrompt.category'),
            width: 150,
        },
        {
            field: 'isActive',
            headerName: t('studyoPrompt.isActive'),
            width: 100,
            renderCell: (params: any) => (
                <span style={{ color: params.row.isActive ? 'green' : 'red' }}>
                    {params.row.isActive ? t('general.active') : t('general.passive')}
                </span>
            ),
        },
        {
            field: 'displayOrder',
            headerName: t('studyoPrompt.displayOrder'),
            width: 100,
        },
        {
            field: 'actions',
            headerName: t('studyoPrompt.actions'),
            width: 120,
            sortable: false,
            renderCell: (params: any) => (
                <Box>
                    <Tooltip title={t('general.edit')}>
                        <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('general.delete')}>
                        <IconButton
                            size="small"
                            onClick={() => handleDelete(params.row.id, params.row.name)}
                            color="error"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <SCUIContent>
            <>
                {isLoading && <SCUILoadingLinear />}
                <SCUIBreadCrumb items={breadcrumbList} />

                <Box className="studyo-prompt-container">
                    <Box className="filters-section">
                        <TextField
                            label={t('studyoPrompt.name')}
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            size="small"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                            style={{ marginRight: 10, minWidth: 200 }}
                        />
                        <TextField
                            select
                            label={t('studyoPrompt.isActive')}
                            value={filterIsActive}
                            onChange={(e) => setFilterIsActive(e.target.value)}
                            size="small"
                            style={{ marginRight: 10, minWidth: 150 }}
                        >
                            <MenuItem value="all">{t('general.all')}</MenuItem>
                            <MenuItem value="active">{t('general.active')}</MenuItem>
                            <MenuItem value="inactive">{t('general.passive')}</MenuItem>
                        </TextField>
                        <SCUIButton
                            text={t('studyoPrompt.search')}
                            type="primary"
                            size="m"
                            onClick={handleSearch}
                            style={{ marginRight: 10 }}
                            sizeType={'fixed'}
                        />
                        <SCUIButton
                            text={t('studyoPrompt.clear')}
                            type="secondary"
                            size="m"
                            onClick={handleClearFilters}
                            sizeType={'fixed'}
                        />
                    </Box>

                    <Box className="actions-section">
                        <SCUIButton
                            text={t('studyoPrompt.add')}
                            type="primary"
                            size="m"
                            onClick={handleAdd}
                            showStartIcon
                            startIcon={<AddIcon />}
                            sizeType={'fixed'}
                        />
                    </Box>

                    <Box className="grid-section">
                        <SCUICustomDataGrid
                            rows={data}
                            columns={columns}
                            pageSize={pageSize}
                            onPageSizeChange={setPageSize}
                            page={currentPage}
                            onPageChange={setCurrentPage}
                            rowCount={totalCount}
                            getRowId={(row: any) => row.id}
                            paginationModeChange
                            loading={isLoading}
                        />
                    </Box>
                </Box>

                <StudioPromptDrawer
                    open={drawerOpen}
                    onClose={handleDrawerClose}
                    prompt={selectedPrompt}
                    onSaveSuccess={handleSaveSuccess}
                />
            </>
        </SCUIContent>
    );
};
